/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
import type { Address } from '@ahm/api-wrappers-core';
import { local } from '@ahm/common-helpers';
import { createStore } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { logger } from '../logger';
import type { Item } from '@/api/use-get-menu-order';

const generateCartItemId = (
  itemId: string | number,
  customizations?: CustomizationSelection[]
): string => {
  if (!customizations || customizations.length === 0) {
    return `${itemId}`;
  }

  const customizationStr = customizations
    .map((cust) => {
      if (!cust.selectedOption) return '';
      return `${cust._id}:${cust.selectedOption._id}`;
    })
    .filter((id) => id !== '')
    .sort()
    .join('_');

  return `${itemId}_${customizationStr}`;
};

export type CustomizationOption = {
  _id: string;
  name: string;
  price: number;
};

export type CustomizationSelection = {
  _id: string | number;
  name: string;
  selectedOption?: CustomizationOption;
};

export type CartItem = Pick<Item, '_id' | 'name' | 'price' | 'quantity'> & {
  customizations?: CustomizationSelection[];
  cartItemId: string;
  note?: string;
};

export type CartState = {
  items: Record<string, CartItem>;
  promo_code?: string;
  payment_method?: string;
  address?: Address;
  branchId?: string;
  estimatedItem?: any;
  mode?: 'webview' | 'webstore';
  currentStoreId?: string;
  // Thêm fields để track store context tốt hơn
  storeSlug?: string;
  pageId?: string;
};

export type CartActions = {
  addToCart: (
    item: Pick<Item, '_id' | 'name' | 'price' | 'quantity'> & {
      customizations?: CustomizationSelection[];
      note?: string;
    }
  ) => void;
  removeItemFromCart: (cartItemId: string) => void;
  updateItemInCart: (cartItemId: string, item: Partial<CartItem>) => void;
  clearCart: () => void;
  setPromoCode: (promo_code: string) => void;
  removePromoCode: () => void;
  setPaymentMethod: (payment_method: string) => void;
  removePaymentMethod: () => void;
  setAddress: (address: Address) => void;
  updateRecipientInfo: (
    recipient: Pick<Address, 'contact_name' | 'contact_number'>
  ) => void;
  setBranchId: (branchId: string, storeId?: string) => void;
  setEstimatedItem: (estimatedItem: number) => void;
  clearCartIfDifferentStore: () => void;
  setMode: (mode: 'webview' | 'webstore') => void;
  // Thêm actions để quản lý store context
  setStoreContext: (context: {
    branchId: string;
    storeId: string;
    storeSlug?: string;
    pageId?: string;
  }) => void;
  clearStoreContext: () => void;
  // Action để clear toàn bộ sau khi order thành công
  clearOrderDataExceptAddress: () => void;
};

export type CartStore = CartState & CartActions;

export const defaultInitState: CartState = {
  items: {},
  promo_code: undefined,
  payment_method: undefined,
  address: undefined,
  branchId: undefined,
  estimatedItem: undefined,
  mode: undefined,
  currentStoreId: undefined,
  storeSlug: undefined,
  pageId: undefined,
};

export const initCartStore = (): CartState => {
  return defaultInitState;
};

export const createCartStore = (initState: CartState = defaultInitState) => {
  return createStore<CartStore>()(
    logger(
      devtools(
        persist(
          (set, get) => ({
            ...initState,

            addToCart: (item) => {
              const cartItemId = generateCartItemId(
                item._id,
                item.customizations
              );

              // Check if item exists in the object
              const existingItem = get().items[cartItemId];

              if (existingItem) {
                set({
                  items: {
                    ...get().items,
                    [cartItemId]: {
                      ...existingItem,
                      quantity: existingItem.quantity + item.quantity,
                      note: item.note ?? existingItem.note,
                    },
                  },
                });
              } else {
                // Add new item to the object
                set({
                  items: {
                    ...get().items,
                    [cartItemId]: {
                      ...item,
                      cartItemId,
                    },
                  },
                });
              }
            },
            removeItemFromCart: (cartItemId) => {
              const newItems = Object.entries(get().items)
                .filter(([key]) => key !== cartItemId)
                .reduce<Record<string, CartItem>>(
                  (acc, [key, value]) => ({ ...acc, [key]: value }),
                  {}
                );
              set({ items: newItems });
            },

            updateItemInCart: (cartItemId, item) => {
              if (get().items[cartItemId]) {
                set({
                  items: {
                    ...get().items,
                    [cartItemId]: {
                      ...get().items[cartItemId],
                      ...item,
                    },
                  },
                });
              }
            },

            clearCart: () => {
              set({ items: {} });
            },

            setPromoCode: (promo_code) => {
              set({ promo_code });
            },

            removePromoCode: () => {
              set({ promo_code: undefined });
            },

            setPaymentMethod: (payment_method) => {
              set({ payment_method });
            },

            removePaymentMethod: () => {
              set({ payment_method: undefined });
            },

            setAddress: (address) => {
              set({ address });
            },

            updateRecipientInfo: (recipient) => {
              const currentAddress = get().address;
              if (!currentAddress) return;
              set({
                address: {
                  ...currentAddress,
                  ...recipient,
                },
              });
            },

            setBranchId: (branchId, storeId?) => {
              if (storeId) {
                const prevStoreId = get().currentStoreId;
                if (prevStoreId && prevStoreId !== storeId) {
                  set({
                    items: {},
                    promo_code: undefined,
                    payment_method: undefined,
                    estimatedItem: undefined,
                    currentStoreId: storeId,
                    branchId,
                  });
                } else {
                  set({
                    branchId,
                    currentStoreId: storeId,
                  });
                }
              } else {
                // fallback logic: chỉ kiểm tra branchId như cũ
                const currentBranchId = get().branchId;
                if (currentBranchId && currentBranchId !== branchId) {
                  set({
                    items: {},
                    promo_code: undefined,
                    payment_method: undefined,
                    estimatedItem: undefined,
                    branchId,
                  });
                } else {
                  set({ branchId });
                }
              }
            },

            setEstimatedItem: (estimatedItem) => {
              set({
                estimatedItem,
              });
            },

            clearCartIfDifferentStore: () => {
              // Function này giờ chỉ để compatibility
              // Logic clear cart được handle trong setStoreContext
            },

            setMode: (mode) => {
              set({ mode });
            },

            setStoreContext: (context) => {
              const { branchId, storeId, storeSlug, pageId } = context;
              const currentStoreId = get().currentStoreId;

              // Nếu đây là store khác, clear cart và data
              if (currentStoreId && currentStoreId !== storeId) {
                set({
                  items: {},
                  promo_code: undefined,
                  payment_method: undefined,
                  estimatedItem: undefined,
                  branchId,
                  currentStoreId: storeId,
                  storeSlug,
                  pageId,
                });
              } else {
                // Cùng store, chỉ update context
                set({
                  branchId,
                  currentStoreId: storeId,
                  storeSlug,
                  pageId,
                });
              }
            },

            clearStoreContext: () => {
              set({
                branchId: undefined,
                currentStoreId: undefined,
                storeSlug: undefined,
                pageId: undefined,
              });
            },

            clearOrderDataExceptAddress: () => {
              set({
                items: {},
                promo_code: undefined,
                payment_method: undefined,
                estimatedItem: undefined,
                branchId: undefined,
                currentStoreId: undefined,
                storeSlug: undefined,
                pageId: undefined,
                // Giữ lại address
              });
            },
          }),
          {
            name: 'ahafood.cart',
            storage: createJSONStorage(() => local),
            partialize: (state) => ({
              items: state.items,
              address: state.address,
              promo_code: state.promo_code,
              payment_method: state.payment_method,
              estimatedItem: state.estimatedItem,
              branchId: state.branchId,
              mode: state.mode,
              currentStoreId: state.currentStoreId,
              storeSlug: state.storeSlug,
              pageId: state.pageId,
            }),
          }
        )
      ),
      'ahafood.cart'
    )
  );
};
